import React, { FC, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { AddIndividualView } from "../../contacts/modals/addContact/AddIndividualView";
import AddOrganizationView from "../../contacts/modals/addContact/AddOrganizationView";
import { AddContactFormFrame } from "../../contacts/modals/addContact/components/AddContactFormFrame";
import AddTimeEntry from "../../time/AddTimeEntry";
import StartTimer from "../../time/StartTimer";
import { AddWorkModal } from "../../works/modals/AddWork.modal";
import { actionTarget } from "./actionProposal.utils";
import {
  AskProposal,
  individualWindowValues,
  organizationWindowValues,
  workWindowValues,
} from "./proposal.utils";

/*
 * Opens the app's normal New work / New organization / New individual window
 * pre-filled with an Ask ProCharted proposal. The window does all checks and
 * creates only when the person clicks Create (it then navigates to the new
 * item like it always does).
 */

type Props = {
  proposal: AskProposal;
  /** Cancel / ×: back to the Ask panel, nothing created. */
  onCancel: () => void;
  /** Created: close everything (the window navigates by itself). */
  onCreated: () => void;
  /** A page was opened with the pre-filled window (sequence, repeat, invoice, payment): close the panel quietly. */
  onOpened: () => void;
};

export const ProposalReview: FC<Props> = ({
  proposal,
  onCancel,
  onCreated,
  onOpened,
}) => {
  const navigate = useNavigate();
  const target = useMemo(
    () =>
      proposal.kind === "create_work" || proposal.kind === "create_client"
        ? null
        : actionTarget(proposal),
    [proposal],
  );
  // Sequence / repeat / invoice / payment: the page opens its own window
  // pre-filled (router state); nothing is saved until Save / Start there.
  useEffect(() => {
    if (target?.type !== "navigate") return;
    navigate(target.to, { state: target.state });
    onOpened();
  }, [target, navigate, onOpened]);
  const work = useMemo(
    () => (proposal.kind === "create_work" ? workWindowValues(proposal) : null),
    [proposal],
  );
  const organization = useMemo(
    () =>
      proposal.kind === "create_client" &&
      proposal.fields.type === "organization"
        ? organizationWindowValues(proposal)
        : null,
    [proposal],
  );
  const individual = useMemo(
    () =>
      proposal.kind === "create_client" && proposal.fields.type === "individual"
        ? individualWindowValues(proposal)
        : null,
    [proposal],
  );

  let content: React.ReactNode = null;
  if (target?.type === "time") {
    content = (
      <AddTimeEntry
        closeModal={onCancel}
        initial={target.initial}
        onSaved={onCreated}
      />
    );
  } else if (target?.type === "timer") {
    content = (
      <StartTimer
        isOpen
        closeModal={onCancel}
        defaultContact={target.contact}
        defaultWork={target.work}
        onStarted={onCreated}
      />
    );
  } else if (work) {
    content = (
      <AddWorkModal
        isOpen
        onClose={onCancel}
        defaultContact={work.defaultContact}
        initialValues={work.values}
        initialNote={work.note || undefined}
        initialRepeat={work.repeat}
        initialAutomation={work.automation}
        initialSequence={work.sequence}
        onCreated={onCreated}
      />
    );
  } else if (organization) {
    content = (
      <AddContactFormFrame onBack={onCancel}>
        <AddOrganizationView
          closeModal={onCancel}
          closeAddCustomerOrganizationPopupModal={onCancel}
          initialValues={organization}
          onCreated={onCreated}
        />
      </AddContactFormFrame>
    );
  } else if (individual) {
    content = (
      <AddContactFormFrame onBack={onCancel}>
        <AddIndividualView
          closeModal={onCancel}
          closeAddCustomerPopupModal={onCancel}
          initialValues={individual}
          onCreated={onCreated}
        />
      </AddContactFormFrame>
    );
  }
  return content ? createPortal(content, document.body) : null;
};
